import api from 'api';
import { ApiDownloadButton, ModalButtonWithForm } from 'components/common';
import { FormGroup, RadioGroup } from 'components/common/form';
import { useReduxState } from 'hooks';
import { breakpoints } from 'styles';

import type { DropdownButtonInterface } from 'components/common/dropdown/DropdownButton';
import type { RequestUser } from 'shared-types';

interface Props {
  completionId?: number;
  courseId: number | string;
  disabled?: boolean;
  fromReaderProps?: Partial<DropdownButtonInterface>;
  includeReviews?: boolean;
  isAdmin?: boolean;
  isDropdown?: boolean;
  tooltip?: string;
  user?: Pick<RequestUser, 'email' | 'id' | 'username'>;
}

interface NavigatorWithMsSaveBlob extends Navigator {
  msSaveBlob?: (blob: Blob, filename: string) => void;
}

const FollowUsersDownloadAnswers = ({
  completionId,
  courseId,
  disabled,
  fromReaderProps,
  includeReviews,
  isAdmin = false,
  isDropdown,
  tooltip,
  user
}: Props) => {
  const { language, messages } = useReduxState();

  const handleSubmit = async ({ answerReviews }) => {
    if (!user) return false;

    const filename = `${user.email || user.username}-answers.pdf`;

    const response = await api.get(
      `/admin/users/${user.id}/courses/${courseId}/answers` +
        `?includeReviews=${answerReviews}` +
        (completionId ? `&completionId=${completionId}` : '') +
        `&language=${language}`,
      { responseType: 'blob' }
    );

    // logic from the ApiDownloadButton...
    const blob = new Blob([response.data]);

    const msSaveBlob = (window.navigator as NavigatorWithMsSaveBlob).msSaveBlob;

    if (msSaveBlob) {
      // download file in ie
      msSaveBlob(blob, filename);
    } else {
      // download file by simulating a link click
      const fileUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = fileUrl;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  if (!user) return null;

  return (
    <>
      {isAdmin ? (
        <ModalButtonWithForm
          buttonCategory="success"
          buttonIcon="file-pdf"
          buttonIsDropdown={isDropdown}
          buttonProps={{ disabled, tooltip }}
          buttonSize="header"
          buttonText={messages.profileDownloadAnswers}
          buttonTextHideBreakpoint={breakpoints.small - 1}
          modalTitle={messages.profileDownloadAnswers}
          modalActionText={messages.commonDownload}
          onSubmit={handleSubmit}
          stopPropagation={true}
        >
          {() => (
            <FormGroup
              htmlFor={`answer-reviews-options-${user.id}`}
              label={messages.profileDownloadAnswersIncludeReviewsQuestion}
            >
              <RadioGroup
                id={`answer-reviews-options-${user.id}`}
                field="answerReviews"
                initialValue={true}
                options={[
                  {
                    id: `with-reviews-${user.id}`,
                    label: messages.profileDownloadAnswersAndReviews,
                    value: true
                  },
                  {
                    id: `no-reviews-${user.id}`,
                    label: messages.profileDownloadAnswersOnly,
                    value: false
                  }
                ]}
              />
            </FormGroup>
          )}
        </ModalButtonWithForm>
      ) : (
        <ApiDownloadButton
          filename={`${user.email || user.username}-answers.pdf`}
          icon="file-pdf"
          isDropdown={isDropdown}
          text={messages.profileDownloadAnswers}
          url={
            `/admin/users/${user.id}/courses/${courseId}/answers` +
            `?includeReviews=${includeReviews}` +
            (completionId ? `&completionId=${completionId}` : '') +
            `&language=${language}`
          }
          {...(fromReaderProps ?? {})}
        />
      )}
    </>
  );
};

export default FollowUsersDownloadAnswers;
